// Card utilities
export const suits = ["♠", "♥", "♦", "♣"];
export const ranks = [
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "10",
  "J",
  "Q",
  "K",
  "A",
];

// Assign values to ranks, with 2 being the lowest and Ace the highest
export const rankValues = ranks.reduce(
  (acc, rank, idx) => ({ ...acc, [rank]: idx + 2 }),
  {}
);

// Create a standard 52-card deck
export function createDeck() {
  return suits.flatMap((suit) =>
    ranks.map((rank) => ({ suit, rank, value: rankValues[rank] }))
  );
}

// Shuffle function to randomize the deck
export function shuffle(array) {
  // Step 1: Make a shallow copy of the input array
  const arr = [...array];

  // Step 2: Iterate over the array in reverse order
  for (let i = arr.length - 1; i > 0; i--) {
    // Step 3: Generate a random index from 0 to i (inclusive) to move the current card to
    const j = Math.floor(Math.random() * (i + 1));

    // one-liner for swapping without needing a temp variable.
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

export function initializeGame() {
  // create and shuffle the deck, then split it into two decks
  const deck = shuffle(createDeck());
  return {
    kellyanDeck: deck.slice(0, deck.length / 2),
    garrettDeck: deck.slice(deck.length / 2),
    warPile: [],
  };
}

// move to helpers later
export function getFirstMessage(gameState) {
  const kCard = gameState.kellyanDownCards[0];
  const gCard = gameState.garrettDownCards[0];

  if (kCard.value > gCard.value) {
    return "Kellyan wins the round!";
  } else if (gCard.value > kCard.value) {
    return "Garrett wins the round!";
  } else {
    return "It's a tie! War!";
  }
}

// Function to get the first message based on the current state
export function getWarMessage(prevState) {
  const kFinalCard = prevState.kellyanDownCards.at(-1);
  const gFinalCard = prevState.garrettDownCards.at(-1);

  const winner = kFinalCard.value >= gFinalCard.value ? "Kellyan" : "Garrett";

  const message =
    kFinalCard.value === gFinalCard.value
      ? "Tie in war goes to Kellyan!"
      : `${winner} wins the war!`;

  return message;
}

// move to helpers later
export function getGameOverMessage(kNext, gNext) {
  if (!kNext && !gNext) {
    return "Both players are out of cards!";
  } else if (!kNext) {
    return "Kellyan is out of cards!<br/> Garrett wins the game!";
  } else if (!gNext) {
    return "Garrett is out of cards!<br/> Kellyan wins the game!";
  } else {
    return ""; // Not game over
  }
}

export function revealLastCard(visibilityArray) {
  const lastIndex = visibilityArray.length - 1;
  if (lastIndex >= 0) {
    visibilityArray[lastIndex] = true;
  }
  return visibilityArray;
}

export function resolveRound(setGameState, setStepPhase) {
  setGameState((prev) => {
    const kCard = prev.kellyanDownCards[0];
    const gCard = prev.garrettDownCards[0];
    const newState = { ...prev, message: "" };
    let nextPhase = "";

    // handle the case without a war
    if (kCard.value !== gCard.value) {
      const downCards = [kCard, gCard];
      if (kCard.value > gCard.value) {
        newState.kellyanEarnedCards = [
          ...prev.kellyanEarnedCards,
          ...downCards,
        ];
      } else {
        newState.garrettEarnedCards = [
          ...prev.garrettEarnedCards,
          ...downCards,
        ];
      }
      // reset down cards and visibility
      newState.kellyanDownCards = [];
      newState.garrettDownCards = [];
      newState.kellyanCardVisiblity = [];
      newState.garrettCardVisiblity = [];

      // handle shuffling earned cards if a deck is empty
      if (
        newState.kellyanDeck.length === 0 &&
        newState.kellyanEarnedCards.length > 0
      ) {
        newState.kellyanDeck = shuffle(newState.kellyanEarnedCards);
        newState.kellyanEarnedCards = [];
      }
      if (
        newState.garrettDeck.length === 0 &&
        newState.garrettEarnedCards.length > 0
      ) {
        newState.garrettDeck = shuffle(newState.garrettEarnedCards);
        newState.garrettEarnedCards = [];
      }

      if (newState.kellyanDeck.length && newState.garrettDeck.length) {
        newState.kellyanDownCards = [newState.kellyanDeck[0]];
        newState.garrettDownCards = [newState.garrettDeck[0]];
        newState.kellyanCardVisiblity = [false];
        newState.garrettCardVisiblity = [false];
        newState.kellyanDeck = newState.kellyanDeck.slice(1);
        newState.garrettDeck = newState.garrettDeck.slice(1);
        nextPhase = "flip_kellyan";
      } else {
        newState.message = getGameOverMessage(
          newState.kellyanDeck[0],
          newState.garrettDeck[0]
        );
        nextPhase = "game_over";
      }
    } else {
      const kDeck = shuffle([...prev.kellyanDeck, ...prev.kellyanEarnedCards]);
      const gDeck = shuffle([...prev.garrettDeck, ...prev.garrettEarnedCards]);
      const kWarCards = kDeck.slice(0, 4);
      const gWarCards = gDeck.slice(0, 4);
      newState.kellyanDownCards = [kCard, ...kWarCards];
      newState.garrettDownCards = [gCard, ...gWarCards];
      newState.kellyanCardVisiblity = [true, ...kWarCards.map(() => false)];
      newState.garrettCardVisiblity = [true, ...gWarCards.map(() => false)];
      newState.kellyanDeck = kDeck.slice(4);
      newState.garrettDeck = gDeck.slice(4);
      newState.kellyanEarnedCards = [];
      newState.garrettEarnedCards = [];

      // check to see if either player is out of cards
      if (
        newState.kellyanDeck.length === 0 &&
        newState.kellyanDownCards.length === 1
      ) {
        newState.message =
          "Kellyan doesn't have a card for war<br/> Garrett wins the game!";
        nextPhase = "game_over";
      } else if (
        newState.garrettDeck.length === 0 &&
        newState.garrettDownCards.length === 1
      ) {
        newState.message =
          "Garrett doesn't have a card for war<br/> Kellyan wins the game!";
        nextPhase = "game_over";
      } else {
        nextPhase = "war_flip_k";
      }
    }

    setStepPhase(nextPhase);
    return newState;
  });
}

export function cleanupWar(setGameState, setStepPhase) {
  setGameState((prev) => {
    const warPile = [...prev.kellyanDownCards, ...prev.garrettDownCards];
    const newState = { ...prev, message: "" };

    // get the cards to compare
    const kNumDownCards = prev.kellyanDownCards.length;
    const gNumDownCards = prev.garrettDownCards.length;
    const kCard = prev.kellyanDownCards[kNumDownCards - 1];
    const gCard = prev.garrettDownCards[gNumDownCards - 1];

    // Give earned cards to winner of the round. Note: tie goes to Kellyan
    if (kCard.value >= gCard.value) {
      newState.kellyanEarnedCards = [...prev.kellyanEarnedCards, ...warPile];
    } else {
      newState.garrettEarnedCards = [...prev.garrettEarnedCards, ...warPile];
    }

    // reset down cards and visibility
    newState.kellyanDownCards = [];
    newState.garrettDownCards = [];
    newState.kellyanCardVisiblity = [];
    newState.garrettCardVisiblity = [];

    // handle shuffling earned cards if a deck is empty
    if (
      newState.kellyanDeck.length === 0 &&
      newState.kellyanEarnedCards.length > 0
    ) {
      newState.kellyanDeck = shuffle(newState.kellyanEarnedCards);
      newState.kellyanEarnedCards = [];
    }
    if (
      newState.garrettDeck.length === 0 &&
      newState.garrettEarnedCards.length > 0
    ) {
      newState.garrettDeck = shuffle(newState.garrettEarnedCards);
      newState.garrettEarnedCards = [];
    }

    if (newState.kellyanDeck.length && newState.garrettDeck.length) {
      newState.kellyanDownCards = [newState.kellyanDeck[0]];
      newState.garrettDownCards = [newState.garrettDeck[0]];
      newState.kellyanCardVisiblity = [false];
      newState.garrettCardVisiblity = [false];
      newState.kellyanDeck = newState.kellyanDeck.slice(1);
      newState.garrettDeck = newState.garrettDeck.slice(1);
      setStepPhase("flip_kellyan");
    } else {
      newState.message = getGameOverMessage(
        newState.kellyanDeck[0],
        newState.garrettDeck[0]
      );
      setStepPhase("game_over");
    }
    return newState;
  });
}

export function startNewGame(
  setGameState,
  setStepPhase,
  setIsSimulating,
  setPreviousStepPhase
) {
  const { kellyanDeck, garrettDeck } = initializeGame();
  const kCard = kellyanDeck[0];
  const gCard = garrettDeck[0];
  setGameState({
    kellyanDeck: kellyanDeck.slice(1),
    garrettDeck: garrettDeck.slice(1),
    kellyanDownCards: [kCard],
    garrettDownCards: [gCard],
    kellyanCardVisiblity: [false],
    garrettCardVisiblity: [false],
    kellyanEarnedCards: [],
    garrettEarnedCards: [],
    message: "",
  });
  setStepPhase("flip_kellyan");
  setIsSimulating(false);
  setPreviousStepPhase(null);
}
